import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ClipboardList, Lock, User } from 'lucide-react';
import { OrderTimelinePListDto } from '../../../../../types/orderTimeline';

interface TimelinePickingListsProps {
  pickingLists: OrderTimelinePListDto[];
}

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

export const TimelinePickingLists: React.FC<TimelinePickingListsProps> = ({ pickingLists }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedPList, setExpandedPList] = useState<number | null>(null);

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <ClipboardList className="w-4 h-4" />
        Plukklister ({pickingLists.length})
      </button>

      {expanded && (
        <div className="space-y-2">
          {pickingLists.map((plist) => {
            const isExpanded = expandedPList === plist.pListID;
            const statusColor = plist.pListStatus >= 60 ? 'bg-green-100 text-green-700 border-green-200' :
              plist.pListStatus >= 50 ? 'bg-blue-100 text-blue-700 border-blue-200' :
              plist.pListStatus >= 40 ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
              'bg-gray-100 text-gray-600 border-gray-200';

            return (
              <div key={plist.pListID} className="bg-white rounded-lg border border-gray-200">
                {/* PList header */}
                <button
                  onClick={() => setExpandedPList(isExpanded ? null : plist.pListID)}
                  className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                    <span className="text-sm font-medium text-gray-800">PList #{plist.pListID}</span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${statusColor}`}>
                      {plist.pListStatusText}
                    </span>
                    {plist.lockStatus > 0 && (
                      <span className="flex items-center gap-1 text-[10px] text-orange-600">
                        <Lock className="w-3 h-3" />
                        {plist.lockedBy || 'Låst'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {plist.operatorID && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {plist.operatorID}
                      </span>
                    )}
                    <span>{plist.numOfOrderLinesPicked}/{plist.numOfOrderLines} linjer</span>
                    {plist.startWorkDateTime && <span>Start: {formatDateTime(plist.startWorkDateTime)}</span>}
                    {plist.stopWorkDateTime && <span>Slutt: {formatDateTime(plist.stopWorkDateTime)}</span>}
                  </div>
                </button>

                {/* PList details */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-gray-100">
                    {/* Meta */}
                    <div className="flex flex-wrap gap-4 py-2 text-xs text-gray-500">
                      {plist.carrierName && <span>Transportør: <strong className="text-gray-700">{plist.carrierName}</strong></span>}
                      {plist.numOfPackages > 0 && <span>Kolli: <strong className="text-gray-700">{plist.numOfPackages}</strong></span>}
                      {plist.deliveryDate && <span>Leveringsdato: <strong className="text-gray-700">{formatDateTime(plist.deliveryDate)}</strong></span>}
                      {plist.created && <span>Opprettet: {formatDateTime(plist.created)} {plist.createdBy && `av ${plist.createdBy}`}</span>}
                      {plist.lastUpdate && <span>Sist oppdatert: {formatDateTime(plist.lastUpdate)} {plist.updatedBy && `av ${plist.updatedBy}`}</span>}
                    </div>

                    {/* PList Lines */}
                    {plist.lines.length > 0 && (
                      <div className="overflow-x-auto mt-1">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600">
                              <th className="text-left px-2 py-1 font-medium border-b border-gray-200">Linje</th>
                              <th className="text-left px-2 py-1 font-medium border-b border-gray-200">OrdreL.</th>
                              <th className="text-left px-2 py-1 font-medium border-b border-gray-200">Varenr</th>
                              <th className="text-left px-2 py-1 font-medium border-b border-gray-200">Lokasjon</th>
                              <th className="text-right px-2 py-1 font-medium border-b border-gray-200">Antall</th>
                              <th className="text-right px-2 py-1 font-medium border-b border-gray-200">Plukket</th>
                              <th className="text-right px-2 py-1 font-medium border-b border-gray-200">Reservert</th>
                              <th className="text-left px-2 py-1 font-medium border-b border-gray-200">Status</th>
                              <th className="text-left px-2 py-1 font-medium border-b border-gray-200">Oppdatert</th>
                            </tr>
                          </thead>
                          <tbody>
                            {plist.lines.map((line) => (
                              <tr key={line.id} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="px-2 py-1 text-gray-600">{line.pListLineNo}</td>
                                <td className="px-2 py-1 text-gray-600">{line.orderNo}-{line.lineNo}</td>
                                <td className="px-2 py-1 font-mono text-gray-800">{line.itemNo || '–'}</td>
                                <td className="px-2 py-1 font-mono text-[10px] text-gray-600">{line.binCode || '–'}</td>
                                <td className="px-2 py-1 text-right text-gray-800">{line.qty}</td>
                                <td className={`px-2 py-1 text-right font-medium ${line.qtyPicked < line.qty ? 'text-yellow-700' : 'text-green-700'}`}>
                                  {line.qtyPicked}
                                </td>
                                <td className="px-2 py-1 text-right text-gray-600">{line.qtyReserved}</td>
                                <td className="px-2 py-1">
                                  <span className={`px-1 py-0.5 rounded text-[10px] ${
                                    line.lineStatus >= 40 ? 'bg-green-100 text-green-700' :
                                    line.lineStatus >= 20 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {line.lineStatus}
                                  </span>
                                </td>
                                <td className="px-2 py-1 text-gray-400">{formatDateTime(line.lastUpdate)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
